import {
  useState, useEffect, useCallback, useRef,
} from 'react'
import {
  Mail, Loader2, Check, Copy, ExternalLink, Pencil, AlertTriangle,
} from 'lucide-react'
import {
  assistantEmailService,
  getApiErrorMessage,
} from '../services/api'
import {
  DNS_VERIFY_POLL_MAX_MS,
  DNS_VERIFY_POLL_MIN_MS,
  usePollWithBackoff,
} from '../hooks/usePollWithBackoff'
import type {
  AssistantEmailOption,
  AssistantEmailStatusResponse,
  AssistantMailboxProvider,
  AssistantMailboxType,
  PendingSubdomainInfo,
} from '../services/api'

// Each option answers three questions in as few words as possible: what it is
// (title), what the address looks like (the per-option `address` the API sends,
// concrete for the active one and a preview for the rest), and what it takes to
// set up (setup).
const OPTION_LABELS: Record<AssistantMailboxType, { title: string; setup: string }> = {
  user_alias: {
    title: 'Plus address on your inbox',
    setup: 'No setup',
  },
  catch_subdomain: {
    title: 'Subdomain of your company domain',
    setup: 'IT adds DNS records',
  },
  catch_owned: {
    title: 'Address on a Catch domain',
    setup: 'No setup, instant',
  },
  external: {
    title: 'A mailbox of your own',
    setup: 'IT creates it, you connect it',
  },
}

const CONNECT_ERROR_MESSAGES: Record<string, string> = {
  primary_account:
    "That's your own work account — connect the separate mailbox your IT created for the assistant.",
  limit:
    'An assistant mailbox is already connected — switch to it, or disconnect it first.',
  kind_conflict:
    "That account is already connected as an additional inbox — disconnect it there first.",
  address_taken:
    'That address is already in use as an assistant mailbox. Reach out to us if that seems wrong.',
  missing_permissions:
    "That sign-in didn't grant everything the assistant needs to read and send from that mailbox — try again, keeping all the permission boxes checked.",
  error: "Connecting didn't finish — no changes were made. Feel free to try again.",
}

// Hosted modes whose local part the user can rename.
const HOSTED_TYPES: ReadonlySet<AssistantMailboxType> = new Set([
  'catch_subdomain',
  'catch_owned',
])

function tokenFromMagicLink(magicLink: string): string | null {
  try {
    return new URL(magicLink).searchParams.get('token')
  } catch {
    return null
  }
}

function PendingSubdomainPanel(
  // `onActivated` returns a promise so the panel can latch on the refresh
  // landing rather than on dispatching it — see `verify` below.
  { pending, onActivated }: {
    pending: PendingSubdomainInfo
    onActivated: () => Promise<void>
  },
): JSX.Element {
  const [copied, setCopied] = useState(false)
  const [verifying, setVerifying] = useState(false)
  // Latches once DNS resolves. The parent does unmount this panel when its
  // refresh lands and `pending_subdomain` clears — but that is an async round
  // trip, and without this the poll keeps re-verifying an already-verified
  // domain across it.
  const [verified, setVerified] = useState(false)
  const token = tokenFromMagicLink(pending.magic_link)

  // The parent passes `onActivated` as an inline arrow, so it is a fresh
  // function on every one of its renders — kept in a ref so `verify`'s identity
  // doesn't churn with it.
  const onActivatedRef = useRef(onActivated)
  useEffect(() => {
    onActivatedRef.current = onActivated
  }, [onActivated])

  const verify = useCallback(async () => {
    if (!token) return
    setVerifying(true)
    try {
      const result = await assistantEmailService.verifySubdomain(token)
      if (result.verified) {
        // Latch on the refresh LANDING, not on dispatching it. The parent's
        // refresh is what clears `pending_subdomain` and unmounts this panel;
        // if it fails, the panel stays mounted, and latching early would stop
        // the poll forever with nothing to restart it — a worse failure than
        // the duplicate polls the latch exists to avoid. Awaiting also keeps
        // the rejection from escaping as an unhandled one.
        await onActivatedRef.current()
        setVerified(true)
      }
    } catch (error) {
      console.error('Verification check failed:', error)
    } finally {
      setVerifying(false)
    }
  }, [token])

  // Poll while we wait for IT to add the delegation, backing off as we go.
  usePollWithBackoff({
    enabled: Boolean(token) && !verified,
    onPoll: verify,
    minMs: DNS_VERIFY_POLL_MIN_MS,
    maxMs: DNS_VERIFY_POLL_MAX_MS,
  })

  const copyLink = async () => {
    await navigator.clipboard.writeText(pending.magic_link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="mt-4 rounded-xl border border-yellow-200 bg-yellow-50 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Loader2 className="w-4 h-4 text-yellow-600 animate-spin" />
        <span className="text-sm font-semibold text-yellow-800">
          Waiting for DNS setup — {pending.address}
        </span>
      </div>
      <p className="text-sm text-yellow-800 mb-3">
        Send this link to your IT team. It walks them through delegating{' '}
        <span className="font-mono">{pending.domain}</span> to Catch by adding the
        nameserver records below.
      </p>

      <div className="flex items-center gap-2 mb-3">
        <input
          readOnly
          value={pending.magic_link}
          className="flex-1 font-mono text-xs bg-white border border-yellow-200 rounded-lg px-3 py-2 text-gray-700"
        />
        <button
          type="button"
          onClick={() => void copyLink()}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-yellow-800 bg-white border border-yellow-200 rounded-lg hover:bg-yellow-100"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {pending.nameservers.length > 0 && (
        <div className="mb-3">
          <p className="text-xs uppercase tracking-wide text-yellow-700 mb-1">
            NS records for {pending.domain}
          </p>
          <ul className="space-y-1">
            {pending.nameservers.map((ns) => (
              <li key={ns} className="font-mono text-xs text-gray-700 bg-white border border-yellow-200 rounded px-2 py-1">
                {ns}
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={() => void verify()}
        disabled={verifying}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg disabled:opacity-50"
      >
        {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
        Verify connection
      </button>
    </div>
  )
}

function ConfirmChangeDialog(
  {
    oldAddress, newAddress, saving, error, onConfirm, onCancel,
  }: {
    oldAddress: string
    newAddress?: string
    saving: boolean
    error?: string | null
    onConfirm: () => void
    onCancel: () => void
  },
): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          <h4 className="text-base font-semibold text-gray-900">Change your address?</h4>
        </div>
        <p className="text-sm text-gray-600">
          {newAddress && (
            <>
              Your assistant will start using{' '}
              <span className="font-mono text-gray-900">{newAddress}</span>.{' '}
            </>
          )}
          Mail to{' '}
          <span className="font-mono text-gray-900">{oldAddress}</span> still reaches
          your assistant for now — your three most recent addresses stay active, and
          changing again releases the oldest.
        </p>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Change address
          </button>
        </div>
      </div>
    </div>
  )
}

// Mirrors the backend normalize_assistant_email_handle rules for inline feedback
// (the server stays the source of truth). Returns an error message, or null.
const HANDLE_MIN = 3
const HANDLE_MAX = 32
const HANDLE_RE = /^[a-z0-9][a-z0-9._-]*[a-z0-9]$/

function validateHandle(raw: string): string | null {
  const handle = raw.trim().toLowerCase()
  if (handle.length < HANDLE_MIN || handle.length > HANDLE_MAX) {
    return `Use ${HANDLE_MIN}–${HANDLE_MAX} characters.`
  }
  if (!HANDLE_RE.test(handle)) {
    return 'Use letters, numbers, dots, hyphens or underscores; '
      + 'start and end with a letter or number.'
  }
  if (handle.includes('..')) return "Handle can't contain consecutive dots."
  return null
}

function HandleEditor(
  {
    handle, domain, changesRemaining, onChanged,
  }: {
    handle: string
    domain: string
    changesRemaining: number
    onChanged: (next: AssistantEmailStatusResponse) => void
  },
): JSX.Element {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(handle)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [saving, setSaving] = useState(false)

  const limitReached = changesRemaining <= 0
  const trimmed = value.trim()
  const changed = trimmed.toLowerCase() !== handle.toLowerCase()
  // Only nag once the user has typed a different, non-empty handle.
  const validationError = changed && trimmed.length > 0 ? validateHandle(value) : null
  const canSubmit = changed && validationError === null

  const startEditing = () => {
    setValue(handle)
    setError(null)
    setEditing(true)
  }

  const cancel = () => {
    setEditing(false)
    setError(null)
  }

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const next = await assistantEmailService.changeHandle(trimmed)
      onChanged(next)
      setEditing(false)
      setConfirming(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change the handle.')
      setConfirming(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-gray-400">Handle</span>
        <span className="text-xs text-gray-400">
          {changesRemaining} change{changesRemaining === 1 ? '' : 's'} left today
        </span>
      </div>

      {!editing ? (
        <div className="mt-1 flex items-center justify-between gap-3">
          <p className="font-mono text-sm text-gray-900 break-all">{handle}@{domain}</p>
          <button
            type="button"
            onClick={startEditing}
            disabled={limitReached}
            title={limitReached ? 'Daily limit reached — try again tomorrow' : undefined}
            className="inline-flex shrink-0 items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Pencil className="w-3.5 h-3.5" />
            Change
          </button>
        </div>
      ) : (
        <div className="mt-2">
          <div className="flex items-stretch">
            <input
              autoFocus
              value={value}
              onChange={(e) => {
                setValue(e.target.value)
                setError(null) // drop any stale server error as the user edits
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSubmit) setConfirming(true)
              }}
              placeholder="handle"
              className="min-w-0 flex-1 font-mono text-sm border border-gray-200 rounded-l-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            <span className="inline-flex items-center px-3 font-mono text-sm text-gray-500 bg-gray-50 border border-l-0 border-gray-200 rounded-r-lg">
              @{domain}
            </span>
          </div>
          {(error ?? validationError) && (
            <p className="mt-2 text-sm text-red-600">{error ?? validationError}</p>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={cancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={!canSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </button>
          </div>
        </div>
      )}

      {confirming && (
        <ConfirmChangeDialog
          oldAddress={`${handle}@${domain}`}
          newAddress={`${trimmed}@${domain}`}
          saving={saving}
          onConfirm={() => void save()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  )
}

// A listbox-style picker (instead of a native <select>) so every option can
// carry its address and setup cost in the list, not just after it's chosen.
function AddressTypeSelect(
  {
    options, value, busy, activeAddress, externalState, onSelect,
  }: {
    options: AssistantEmailOption[]
    value: AssistantMailboxType
    busy: boolean
    // The address actually in effect, for the selected row — the API previews
    // a hosted address the user may not have been given yet (a number can be
    // taken between the preview and the write). Null while a setup is pending.
    activeAddress: string | null
    externalState: 'none' | 'connected' | 'needs_reauth'
    onSelect: (type: AssistantMailboxType) => void
  },
): JSX.Element {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const selected = OPTION_LABELS[value]
  // A connected mailbox costs nothing to set up, so its badge reports the
  // connection instead — including when it needs a refresh, so it can't read
  // "Connected" above a panel asking the user to reconnect.
  const setupFor = (type: AssistantMailboxType): string => {
    if (type !== 'external' || externalState === 'none') {
      return OPTION_LABELS[type].setup
    }
    return externalState === 'needs_reauth' ? 'Needs reconnect' : 'Connected'
  }

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        id="assistant-email-type"
        disabled={busy}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:opacity-60"
      >
        <span className="block text-sm text-gray-900">{selected.title}</span>
        <span className="mt-0.5 block text-xs text-gray-500">{setupFor(value)}</span>
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
          {busy ? (
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
          ) : (
            <svg className="w-4 h-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.085l3.71-3.855a.75.75 0 111.08 1.04l-4.25 4.41a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          )}
        </span>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-10 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
        >
          {options.map((option) => {
            const { title } = OPTION_LABELS[option.type]
            const isSelected = option.type === value
            const isDisabled = !option.available && !isSelected
            // Preview addresses stand in for one that doesn't exist yet.
            const address =
              isSelected && activeAddress != null ? activeAddress : option.address
            return (
              <li key={option.type} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={() => {
                    setOpen(false)
                    if (!isSelected) onSelect(option.type)
                  }}
                  className={`block w-full px-4 py-3 text-left hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 ${
                    isSelected ? 'bg-primary-50' : ''
                  }`}
                >
                  {/* wraps rather than squeezing the badge on narrow screens */}
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-medium text-gray-900">{title}</span>
                    {isSelected && <Check className="h-4 w-4 shrink-0 text-primary-600" />}
                    <span className="ml-auto shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {setupFor(option.type)}
                    </span>
                  </span>
                  <span className="mt-1 block break-all font-mono text-xs text-gray-500">
                    {address}
                  </span>
                  {!option.available && option.note && (
                    <span className="mt-1 block text-xs text-amber-700">{option.note}</span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function ConnectProviderPanel(
  { onChoose, onCancel }: {
    onChoose: (provider: AssistantMailboxProvider) => void
    onCancel: () => void
  },
): JSX.Element {
  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <p className="text-sm font-medium text-gray-900">
        Where does the assistant’s mailbox live?
      </p>
      <p className="mt-1 text-sm text-gray-600">
        Sign in as the mailbox your IT created for your assistant — not as
        yourself.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onChoose('google')}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg"
        >
          Google Workspace
        </button>
        <button
          type="button"
          onClick={() => onChoose('microsoft')}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg"
        >
          Microsoft 365
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

export function AssistantEmailSection(): JSX.Element {
  const [status, setStatus] = useState<AssistantEmailStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  // A mode the user picked in the dropdown but hasn't confirmed yet — switching
  // modes retires the current address, so it goes through the same warning.
  const [pendingMode, setPendingMode] = useState<AssistantMailboxType | null>(null)
  const [modeError, setModeError] = useState<string | null>(null)
  // Outcome of the assistant-mailbox OAuth redirect (?assistant_email_connect=).
  const [connectError, setConnectError] = useState<string | null>(null)
  // Two-step disconnect for the connected assistant mailbox (revokes the
  // stored OAuth grant, not just switches away from it).
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  // Shown when "a mailbox of your own" is picked with nothing connected yet.
  // The provider is the user's to state: it belongs to the mailbox their IT
  // created, which needn't match the account they signed in to Catch with.
  const [choosingProvider, setChoosingProvider] = useState(false)

  const refresh = useCallback(async () => {
    setStatus(await assistantEmailService.getStatus())
    // Fresh server state supersedes a pending provider choice — most visibly
    // when the OAuth callback has just connected an account.
    setChoosingProvider(false)
  }, [])

  useEffect(() => {
    refresh()
      .catch((err) => console.error('Failed to load assistant email status:', err))
      .finally(() => setLoading(false))
  }, [refresh])

  // Handle the connect-assistant-mailbox OAuth callback return.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const result = params.get('assistant_email_connect')
    if (!result) return
    if (result !== 'success') {
      setConnectError(
        CONNECT_ERROR_MESSAGES[result] ?? CONNECT_ERROR_MESSAGES['error'] ?? null,
      )
    }
    params.delete('assistant_email_connect')
    const query = params.toString()
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}`,
    )
  }, [])

  const startAssistantMailboxConnect = async (
    provider: AssistantMailboxProvider,
  ) => {
    window.location.href =
      await assistantEmailService.getConnectAssistantMailboxUrl(provider)
  }

  const disconnectAssistantMailbox = async () => {
    if (!status?.external_account) return
    setDisconnecting(true)
    setConnectError(null)
    try {
      // Server side this deletes the account (revoking our stored tokens) and,
      // if the mailbox was the active identity, reverts to the plus address.
      await assistantEmailService.disconnectAssistantMailbox(
        status.external_account.account_id,
      )
      setConfirmDisconnect(false)
      await refresh()
    } catch (error) {
      setConnectError(
        getApiErrorMessage(error, 'Could not disconnect the assistant mailbox.'),
      )
    } finally {
      setDisconnecting(false)
    }
  }

  const handleSelect = async (type: AssistantMailboxType) => {
    if (!status || type === status.mailbox_type) return
    setBusy(true)
    setModeError(null)
    // Any mode change supersedes a provider choice left open for `external`;
    // without this the panel stays visible and clickable for a mode that is
    // no longer selected.
    setChoosingProvider(false)
    try {
      if (type === 'external' && status.external_account == null) {
        // First-time connect: the identity flips server-side on the OAuth
        // callback, so this ends in a redirect rather than a PUT — but which
        // provider to redirect to is the user's to say, so ask first.
        setConnectError(null)
        setChoosingProvider(true)
        return
      }
      setStatus(await assistantEmailService.setMailboxType(type))
    } catch (error) {
      setModeError(getApiErrorMessage(error, 'Could not update your assistant email.'))
      throw error
    } finally {
      setBusy(false)
    }
  }

  const confirmModeChange = async () => {
    if (pendingMode == null) return
    try {
      await handleSelect(pendingMode)
      setPendingMode(null) // only dismiss on success; failures stay visible
    } catch {
      // Error surfaced via modeError; keep the dialog open.
    }
  }

  const cancelModeChange = () => {
    setPendingMode(null)
    setModeError(null)
  }

  // Domain backing the editable handle: a pending subdomain reports its own
  // domain (the current address is still the plus-address until it activates);
  // an active hosted mailbox carries it on current_address.
  const hostedDomain = status?.editable_handle
    ? status.pending_subdomain?.domain ?? status.current_address.split('@')[1] ?? null
    : null
  const showHandleEditor =
    status != null &&
    status.editable_handle != null &&
    hostedDomain != null &&
    HOSTED_TYPES.has(status.mailbox_type)
  // The handle editor already surfaces the live address (with an edit control),
  // so the standalone "Current" box is only worth showing when it would differ —
  // e.g. a pending subdomain whose mail still routes through the plus-address.
  const showCurrentBox =
    status != null &&
    (!showHandleEditor ||
      status.current_address !== `${status.editable_handle}@${hostedDomain}`)

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-1">
        <Mail className="w-5 h-5 text-primary-500" />
        <h3 className="text-lg font-semibold text-gray-900">Assistant email</h3>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        The address your assistant sends and receives mail from.
      </p>

      {connectError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <p className="text-sm text-red-700">{connectError}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
        </div>
      ) : status ? (
        <>
          {showCurrentBox && (
            <div className="mb-4 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
              <span className="text-xs uppercase tracking-wide text-gray-400">Current</span>
              <p className="font-mono text-sm text-gray-900 break-all">{status.current_address}</p>
            </div>
          )}

          <label
            htmlFor="assistant-email-type"
            className="block text-xs uppercase tracking-wide text-gray-400 mb-1"
          >
            Address type
          </label>
          <AddressTypeSelect
            options={status.options}
            value={status.mailbox_type}
            busy={busy}
            // While a subdomain waits on DNS the live address is still the plus
            // address, so the selected row shows its preview instead.
            activeAddress={status.pending_subdomain ? null : status.current_address}
            externalState={
              status.external_account == null
                ? 'none'
                : status.external_account.needs_reauth
                  ? 'needs_reauth'
                  : 'connected'
            }
            onSelect={(next) => {
              setModeError(null)
              setPendingMode(next)
            }}
          />

          {showHandleEditor && status.editable_handle != null && hostedDomain != null && (
            <HandleEditor
              handle={status.editable_handle}
              domain={hostedDomain}
              changesRemaining={status.handle_changes_remaining ?? 0}
              onChanged={setStatus}
            />
          )}

          {status.external_account && (
            <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <span className="text-xs uppercase tracking-wide text-gray-400">
                    Connected mailbox
                    {status.mailbox_type !== 'external' && ' · not in use'}
                  </span>
                  <p className="font-mono text-sm text-gray-900 break-all">
                    {status.external_account.email}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {status.mailbox_type === 'external' &&
                    status.external_account.needs_reauth && (
                      <button
                        type="button"
                        // Reconnecting is re-consent for the SAME mailbox, so
                        // its provider is already known — never re-ask.
                        onClick={() =>
                          void startAssistantMailboxConnect(
                            status.external_account?.provider === 'microsoft'
                              ? 'microsoft'
                              : 'google',
                          )
                        }
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg"
                      >
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Reconnect
                      </button>
                    )}
                  <button
                    type="button"
                    disabled={disconnecting}
                    onClick={() => {
                      setConnectError(null)
                      setConfirmDisconnect(true)
                    }}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white hover:text-red-700 hover:border-red-200 border border-gray-200 rounded-lg disabled:opacity-50"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
              {status.mailbox_type === 'external' &&
                status.external_account.needs_reauth && (
                  <p className="mt-2 text-sm text-amber-800">
                    The connection needs a refresh — until then, your assistant
                    falls back to your plus address for email.
                  </p>
                )}
              {confirmDisconnect && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                  <p className="text-sm text-red-700">
                    This signs the assistant out of{' '}
                    <span className="font-mono">{status.external_account.email}</span>{' '}
                    and removes its access.
                    {status.mailbox_type === 'external' &&
                      ' Your assistant email switches back to your plus address.'}
                  </p>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      disabled={disconnecting}
                      onClick={() => void disconnectAssistantMailbox()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
                    >
                      {disconnecting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      Remove access
                    </button>
                    <button
                      type="button"
                      disabled={disconnecting}
                      onClick={() => setConfirmDisconnect(false)}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg disabled:opacity-50"
                    >
                      Keep it
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {choosingProvider && (
            <ConnectProviderPanel
              onChoose={(provider) => void startAssistantMailboxConnect(provider)}
              onCancel={() => setChoosingProvider(false)}
            />
          )}

          {status.pending_subdomain && (
            <PendingSubdomainPanel
              pending={status.pending_subdomain}
              onActivated={refresh}
            />
          )}

          {pendingMode != null && (
            <ConfirmChangeDialog
              oldAddress={status.current_address}
              saving={busy}
              error={modeError}
              onConfirm={() => void confirmModeChange()}
              onCancel={cancelModeChange}
            />
          )}
        </>
      ) : (
        <p className="text-sm text-gray-500">Couldn’t load your assistant email settings.</p>
      )}
    </div>
  )
}
